import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Cloud, Mail } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailVerificationError, setShowEmailVerificationError] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  // Check if user just verified their email
  const verified = searchParams.get('verified');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowEmailVerificationError(false);

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      toast.success(t('message.loginSuccess'));
      onLogin(response.data.user, {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token
      });
      
      // Redirect based on role
      if (response.data.user.role === 'platform_admin') {
        navigate('/database-admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Login failed';
      
      // Check if error is due to unverified email
      if (error.response?.status === 403 && errorDetail.includes('verify your email')) {
        setShowEmailVerificationError(true);
        toast.error(errorDetail);
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error(t('message.fieldRequired'));
      return;
    }

    setResendingEmail(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, 
        { email },  // Send as JSON object
        { headers: { 'Content-Type': 'application/json' } }
      );
      toast.success(t('message.verificationSent'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResendingEmail(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 bg-gradient-to-br from-cyan-50 via-blue-50 to-slate-50 relative">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl mb-4">
                <Cloud className="text-white" size={32} />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('auth.welcomeBack')}</h1>
              <p className="text-slate-600">{t('auth.signInTo')}</p>
            </div>

            {/* Email Verified Success */}
            {verified && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm font-medium">
                  ✓ {t('message.emailVerified')}
                </p>
              </div>
            )}

            {/* Email Verification Required */}
            {showEmailVerificationError && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                <p className="text-amber-800 text-sm font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {t('auth.emailNotVerified')}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleResendVerification}
                  disabled={resendingEmail}
                  className="w-full"
                >
                  {resendingEmail ? t('common.loading') : t('auth.resendEmail')}
                </Button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
              <div>
                <Label htmlFor="email">{t('form.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  data-testid="login-email-input"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="password">{t('form.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="mt-1.5"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? t('common.loading') : t('btn.login')}
              </Button>

              <div className="text-right">
                <Link to="/forgot-password" className="text-sm text-cyan-600 hover:text-cyan-700 font-medium">
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </form>

            <p className="text-center mt-6 text-sm text-slate-600">
              {t('auth.noAccount')}{' '}
              <Link to="/signup" className="text-cyan-600 hover:text-cyan-700 font-medium" data-testid="signup-link">
                {t('auth.signUp')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-cyan-600 via-blue-600 to-blue-700 items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <h2 className="text-4xl font-bold mb-6">Modern Property Management</h2>
          <p className="text-xl text-cyan-50 mb-8">
            Streamline your hotel operations with our cloud-native PMS. Manage reservations, housekeeping, and guests all in one place.
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-cyan-300 rounded-full"></div>
              <span className="text-cyan-50">Real-time calendar & availability</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-cyan-300 rounded-full"></div>
              <span className="text-cyan-50">Multi-property management</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-cyan-300 rounded-full"></div>
              <span className="text-cyan-50">Automated housekeeping workflows</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
